import {Button, Input, toast} from '@cashiaApp/web-components';
import React, {ChangeEvent, FormEvent, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {CheckoutRequest, checkout} from './api';
import Info from '../../../assets/icons/info-circle.svg';
import {CheckoutBaseFields} from '../../../components/CheckoutBaseFields';
import {CredentialsFields} from '../../../components/CredentialsFields';
import Spinner from '../../../components/tailwind/Spinner';
import {useCredentialsV2} from '../../../hooks/hmac/useHmacV2';
import {
  generateCashiaHeadersV2,
  validateAuthParamsV2,
} from '../../../utils/hmac/cashiaAuthV2';

interface FormValues {
  terminalId: string;
  amount: number;
  currency: string;
  requestId: string;
  successRedirectUrl: string;
  errorRedirectUrl: string;
  webhookUrl: string;
  secretKey: string;
  keyId: string;
}

interface FormErrors {
  [key: string]: string;
}

const CheckoutComponentV2: React.FC = () => {
  const {showPassword, setShowPassword, saveCredentials, getCredentials} =
    useCredentialsV2();

  const storedCreds = getCredentials();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const [submissionType, setSubmissionType] = useState<
    'iframe' | 'redirect' | null
  >(null);

  const [values, setValues] = useState<FormValues>({
    terminalId: storedCreds?.terminalId || 'test',
    amount: 1000,
    currency: 'KES',
    requestId: uuidv4(),
    successRedirectUrl: 'https://www.cashia.com/',
    errorRedirectUrl: 'https://www.cashia.com/',
    webhookUrl: 'https://www.cashia.com/',
    secretKey: storedCreds?.secretKey || 'test',
    keyId: storedCreds?.keyId || 'test',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (formValues: FormValues): FormErrors => {
    const formErrors: FormErrors = {};
    const invalidCharactersPattern = /[ !@#]/;

    // Base required fields
    if (!formValues.amount) formErrors.amount = 'Amount is required';
    if (!formValues.currency.trim()) {
      formErrors.currency = 'Currency is required';
    }
    if (!formValues.requestId.trim()) {
      formErrors.requestId = 'Request is required';
    }
    if (!formValues.terminalId.trim()) {
      formErrors.terminalId = 'Terminal is required';
    }

    // Success URL
    if (!formValues.successRedirectUrl.trim()) {
      formErrors.successRedirectUrl = 'CallbackUrl is required';
    } else if (!isValidUrl(formValues.successRedirectUrl)) {
      formErrors.successRedirectUrl = 'Please enter a valid URL';
    } else if (invalidCharactersPattern.test(formValues.successRedirectUrl)) {
      formErrors.successRedirectUrl =
        'Please enter a valid URL without spaces or !,@,#';
    }

    // Error URL
    if (!formValues.errorRedirectUrl.trim()) {
      formErrors.errorRedirectUrl = 'CallbackUrl is required';
    } else if (!isValidUrl(formValues.errorRedirectUrl)) {
      formErrors.errorRedirectUrl = 'Please enter a valid URL';
    } else if (invalidCharactersPattern.test(formValues.errorRedirectUrl)) {
      formErrors.errorRedirectUrl =
        'Please enter a valid URL without spaces or !,@,#';
    }

    return formErrors;
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setIsSubmitting(true);

    const validationErrors = validate(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    // Save credentials to localStorage
    saveCredentials(values.keyId, values.secretKey, values.terminalId);

    const requestBody: CheckoutRequest = {
      terminalId: values.terminalId,
      requestId: values.requestId,
      amount: Number(values.amount),
      currency: values.currency,
      successRedirectUrl: values.successRedirectUrl,
      errorRedirectUrl: values.errorRedirectUrl,
      webhookUrl: values.webhookUrl,
    };

    try {
      // Validate authentication parameters
      validateAuthParamsV2(values.keyId, values.secretKey);

      // Generate headers using the reusable utility
      const headers = generateCashiaHeadersV2({
        secret: values.secretKey,
        keyId: values.keyId,
        requestBody,
        method: 'POST',
      });

      const checkoutResponse = await checkout(requestBody, headers);

      if (submissionType === 'iframe') {
        setIframeSrc(checkoutResponse.url);
        setShowIframe(true);
      } else {
        window.location.href = checkoutResponse.url;
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const renderErrorMessage = (fieldName: string) => {
    if (errors[fieldName]) {
      return (
        <span className="flex items-center gap-2">
          <img src={Info} alt="Error" />
          <span className="text-orangeDark px-4 rounded text-sm font-normal bg-lightOrange">
            {errors[fieldName]}
          </span>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="h-screen w-screen flex justify-between overflow-y-auto bg-offWhite">
      <div className="flex items-center flex-col w-full px-4 xl:px-0 lg:w-2/5 py-6 md:py-5 lg:py-4 2xl:py-20">
        <div className="flex flex-col flex-grow max-w-md lg:ml-[60px] w-full">
          <form
            onSubmit={handleFormSubmit}
            method="POST"
            className="flex flex-col gap-2">
            {/* Base Checkout Fields */}
            <CheckoutBaseFields
              requestId={values.requestId}
              amount={values.amount}
              currency={values.currency}
              onChange={handleChange}
              renderErrorMessage={renderErrorMessage}
            />

            {/* Additional Fields */}
            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                Success CallbackUrl * {renderErrorMessage('successRedirectUrl')}
              </label>
              <Input
                name="successRedirectUrl"
                value={values.successRedirectUrl}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>

            <div className="my-1">
              <label className="text-darkGrey text-sm font-bold mb-1 flex gap-2 items-center">
                Error CallbackUrl * {renderErrorMessage('errorRedirectUrl')}
              </label>
              <Input
                name="errorRedirectUrl"
                value={values.errorRedirectUrl}
                onChange={handleChange}
                className="w-full bg-transparent rounded-lg h-12 focus:ring-2 focus-visible:border-none focus:ring-[#FECED8]"
              />
            </div>

            {/* Credentials Fields */}
            <CredentialsFields
              keyId={values.keyId}
              secretKey={values.secretKey}
              terminalId={values.terminalId}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              onChange={handleChange}
              renderErrorMessage={renderErrorMessage}
            />

            {/* Submit Buttons */}
            <div className="flex justify-between mt-4 gap-2">
              <Button
                type="submit"
                className="bg-smoothRed text-white w-1/2 h-12 rounded-lg hover:bg-hotPink"
                onClick={() => setSubmissionType('iframe')}
                disabled={isSubmitting}>
                {isLoading && submissionType === 'iframe' ? (
                  <Spinner className="fill-white" />
                ) : (
                  'Iframe'
                )}
              </Button>
              <Button
                type="submit"
                className="bg-smoothRed text-white w-1/2 h-12 rounded-lg hover:bg-hotPink"
                onClick={() => setSubmissionType('redirect')}
                disabled={isSubmitting}>
                {isLoading && submissionType === 'redirect' ? (
                  <Spinner className="fill-white" />
                ) : (
                  'Redirect'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Iframe Content */}
      {showIframe && (
        <div className="w-2/5 p-6 bg-white shadow-lg rounded-lg">
          <iframe src={iframeSrc} width="100%" height="100%" title="Checkout" />
        </div>
      )}
    </div>
  );
};

export default CheckoutComponentV2;
